import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import {
  trackPublicEvent,
  trackPublicVisitor,
} from '../../Admin/Services/GrowthService';
import { TrackEventRequest } from '../../Admin/Types/GrowthType';
import {
  buildEventPayload,
  buildVisitorPayload,
  captureAttribution,
  getConsent,
  getOrCreateVisitorSession,
  getVisitorId,
  hasConsentPreference,
  isPublicRoute,
  setConsentPreference,
} from '../Services/VisitorTracker';

interface AnalyticsContextValue {
  consent: 'granted' | 'denied';
  acceptConsent: () => void;
  declineConsent: () => void;
  track: (eventName: string, extra?: Partial<TrackEventRequest>) => void;
}

const AnalyticsContext = createContext<AnalyticsContextValue | null>(null);

export const useAnalytics = (): AnalyticsContextValue => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics must be used within AnalyticsProvider');
  }
  return context;
};

export function AnalyticsProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const [consent, setConsent] = useState<'granted' | 'denied'>(() => getConsent());
  const [showNotice, setShowNotice] = useState<boolean>(() => !hasConsentPreference());
  const consentRef = useRef(consent);
  const visitorSentRef = useRef(false);

  consentRef.current = consent;

  const track = useCallback(
    (eventName: string, extra?: Partial<TrackEventRequest>) => {
      if (consentRef.current !== 'granted') {
        return;
      }
      const visitorId = getVisitorId();
      void trackPublicEvent(buildEventPayload(visitorId, eventName, extra));
    },
    []
  );

  const ensureVisitor = useCallback(() => {
    if (visitorSentRef.current) {
      return;
    }
    visitorSentRef.current = true;
    const visitorId = getVisitorId();
    const { newSession } = getOrCreateVisitorSession();
    void trackPublicVisitor(buildVisitorPayload(visitorId, newSession));
  }, []);

  const acceptConsent = useCallback(() => {
    setConsentPreference(true);
    setConsent('granted');
    setShowNotice(false);
  }, []);

  const declineConsent = useCallback(() => {
    setConsentPreference(false);
    setConsent('denied');
    setShowNotice(false);
  }, []);

  useEffect(() => {
    captureAttribution();
    if (consent === 'granted') {
      ensureVisitor();
    }
  }, [consent, ensureVisitor]);

  useEffect(() => {
    if (consent !== 'granted' || !isPublicRoute(location.pathname)) {
      return;
    }
    track('page_view');
    if (/^\/(registration|invite)/.test(location.pathname)) {
      track('candidate_page_view');
    }
  }, [location.pathname, consent, track]);

  const showBanner = showNotice && isPublicRoute(location.pathname);

  const value = useMemo<AnalyticsContextValue>(
    () => ({ consent, acceptConsent, declineConsent, track }),
    [consent, acceptConsent, declineConsent, track]
  );

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
      {showBanner && (
        <div className="fixed bottom-4 left-4 z-[100] max-w-sm rounded-2xl border border-slate-700/80 bg-slate-900/95 p-5 shadow-2xl backdrop-blur">
          <p className="text-sm font-bold text-slate-100">We use anonymous analytics</p>
          <p className="mt-1.5 text-xs leading-relaxed text-slate-400">
            To understand how visitors find and use Nexgent, we collect anonymous usage data
            (pages viewed, device type, referring link). No name, email, IP address, or other
            personal data is ever collected. You can turn this off at any time.
          </p>
          <div className="mt-4 flex gap-3">
            <button
              onClick={acceptConsent}
              className="flex-1 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 px-4 py-2.5 text-xs font-black uppercase tracking-wider text-white transition hover:opacity-90"
            >
              Got it
            </button>
            <button
              onClick={declineConsent}
              className="flex-1 rounded-xl border border-slate-700 bg-slate-800/60 px-4 py-2.5 text-xs font-black uppercase tracking-wider text-slate-300 transition hover:border-slate-500"
            >
              Opt out
            </button>
          </div>
        </div>
      )}
    </AnalyticsContext.Provider>
  );
}